/**
 * Publish Flow Service - frontend aggregator for a single publish run.
 *
 * The Publish Flow page is correlationId-driven: it answers
 * "what actually happened during publish {correlationId}?".
 *
 * This service composes existing publish-related endpoints into one coherent
 * view-model. It stays presentation-friendly (plain types, no Vue dependency)
 * so it can be unit-tested without a DOM.
 *
 * Sources composed:
 *  - POST /check-import-feed-status  → ImportFeedJob (status, timestamps, result, artifacts pointer)
 *  - GET  /publish-artifacts/{jazelId}/{correlationId}  → trigger artifacts + finalize merge
 *  - GET  /publish-logs/{jazelId}/{correlationId}  → merged JSONL log entries
 *  - GET  /inventory?action=runMetrics&...  → counts (+optional VINs)
 *  - GET  /publish-flow-metrics/{jazelId}/{correlationId}  → measured stage-by-stage flow metrics
 *
 * Each upstream call is best-effort and degrades gracefully: missing pieces
 * surface as `null` so the UI can render honest empty states instead of
 * inventing data. We never fabricate "stage throughput" numbers — anything
 * that looks like a stage count is sourced verbatim from the run artifacts.
 */

import {
  checkImportFeedJobByCorrelationId,
} from '@/services/publishService'
import {
  getPublishArtifacts,
  getPublishLogs,
  type PublishArtifactsResponse,
  type PublishLogsResponse,
} from '@/services/publishHistoryService'
import { fetchPublishRunMetrics, type PublishRunMetrics } from '@/services/inventory'
import {
  getPublishFlowMetrics,
  type FlowMetricsResponse,
} from '@/services/publishFlowMetricsService'

export interface PublishFlowJob {
  status: string
  triggerType?: string
  requestedByUsername?: string
  feed?: string
  fileKey?: string
  startedAt?: string
  finishedAt?: string
  errorMessage?: string
  result?: any | null
}

export interface PublishFlowFetchResult {
  jazelId: string
  correlationId: string
  job: PublishFlowJob | null
  artifacts: PublishArtifactsResponse | null
  logs: PublishLogsResponse | null
  runMetrics: PublishRunMetrics | null
  runMetricsCounts?: PublishRunMetrics | null
  /**
   * Stage-by-stage measurements (Phase 2). May be `null` for runs that
   * predate flow-metrics instrumentation; the response itself carries a
   * `missingReason: 'NOT_INSTRUMENTED'` marker in that case.
   */
  flowMetrics: FlowMetricsResponse | null
  errors: PublishFlowFetchErrors
}

export interface PublishFlowFetchErrors {
  job?: string
  artifacts?: string
  logs?: string
  runMetrics?: string
  flowMetrics?: string
}

export interface FetchPublishFlowOptions {
  /** Include capped per-bucket VIN lists (defaults to true; capped server-side). */
  includeVins?: boolean
  /** Override the server-side VIN cap (default 500). */
  maxVins?: number
  /** Override the server-side log line cap. */
  maxLogLines?: number
}

/**
 * Fetch every publish-flow source for a given correlationId in parallel.
 *
 * - Uses Promise.allSettled so a single 4xx/5xx never poisons the page.
 * - Returns structured `errors` so the UI can show per-source banners.
 * - Never throws; callers handle empty/error states declaratively.
 */
export async function fetchPublishFlow(
  endpoint: string,
  jazelId: string,
  correlationId: string,
  opts: FetchPublishFlowOptions = {},
): Promise<PublishFlowFetchResult> {
  const includeVins = opts.includeVins !== false
  const maxVins = opts.maxVins ?? 500

  const countsPromise = fetchPublishRunMetrics(endpoint, jazelId, correlationId, { includeVins: false })
  const [jobRes, artifactsRes, logsRes, metricsRes, flowMetricsRes, countsRes] = await Promise.allSettled([
    checkImportFeedJobByCorrelationId(endpoint, correlationId),
    getPublishArtifacts(endpoint, jazelId, correlationId),
    getPublishLogs(endpoint, jazelId, correlationId, opts.maxLogLines ? { maxLines: opts.maxLogLines } : undefined),
    includeVins ? fetchPublishRunMetrics(endpoint, jazelId, correlationId, { includeVins, maxVins }) : countsPromise,
    getPublishFlowMetrics(endpoint, jazelId, correlationId),
    countsPromise,
  ])

  const errors: PublishFlowFetchErrors = {}

  const job: PublishFlowJob | null = (() => {
    if (jobRes.status === 'fulfilled') return jobRes.value
    errors.job = describeError(jobRes.reason)
    return null
  })()

  const artifacts = (() => {
    if (artifactsRes.status === 'fulfilled') return artifactsRes.value
    errors.artifacts = describeError(artifactsRes.reason)
    return null
  })()

  const logs = (() => {
    if (logsRes.status === 'fulfilled') return logsRes.value
    errors.logs = describeError(logsRes.reason)
    return null
  })()

  const runMetrics = (() => {
    if (metricsRes.status === 'fulfilled') return metricsRes.value?.metrics ?? null
    errors.runMetrics = describeError(metricsRes.reason)
    return null
  })()

  const flowMetrics = (() => {
    if (flowMetricsRes.status === 'fulfilled') return flowMetricsRes.value
    errors.flowMetrics = describeError(flowMetricsRes.reason)
    return null
  })()

  return {
    jazelId,
    correlationId,
    job,
    artifacts,
    logs,
    runMetrics,
    runMetricsCounts: countsRes.status === 'fulfilled' ? countsRes.value?.metrics ?? null : null,
    flowMetrics,
    errors,
  }
}

/* -------------------------------------------------------------------------- */
/* Run summary view-model                                                      */
/* -------------------------------------------------------------------------- */

export interface PublishFailureViewModel {
  hasFailure: boolean
  title: string
  stageLabel: string
  reason: string
  expectedFeeds: string[]
  completedFeeds: string[]
  failedFeeds: string[]
  failedFeed: string | null
  inventoryProcessingStarted: boolean | null
  correlationId?: string
}

export interface RunSummaryViewModel {
  status: string
  statusColor: 'success' | 'error' | 'warning' | 'info' | 'grey'
  triggerType: 'user' | 'feed' | 'unknown'
  requestedByUsername: string | null
  startedAt: string | null
  finishedAt: string | null
  durationMs: number | null
  feedName: string | null
  fileKey: string | null
  /** True when the run terminated (success or failure) and a duration is meaningful. */
  isTerminal: boolean
  failure: PublishFailureViewModel
}

export interface PublishOutcomeViewModel {
  hasOutcome: boolean
  liveWebsiteCount: number | null
  added: number
  removed: number
  updated: number
  madeVisible: number
  hidden: number
}

/**
 * Build a presentation-friendly run summary from whatever sources are present.
 *
 * Falls back across job → artifacts so a partial fetch still yields a useful
 * header. Fields the system genuinely doesn't know are returned as `null`.
 */
function stringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.map((v) => String(v || '').trim()).filter(Boolean)
}

function failurePhaseLabel(phase: string, inventoryProcessingStarted: boolean | null): string {
  if (phase === 'trigger_import_feed_worker' || inventoryProcessingStarted === false) {
    return 'Before feed processing'
  }
  if (phase === 'inventory_controller') return 'Feed preparation'
  if (phase === 'import_feed_handler') return 'Inventory processing'
  if (phase === 'publish_wide_finalize') return 'Website publish finalization'
  return 'Unknown stage'
}

export function buildFailureViewModel(input: {
  status: string
  job: PublishFlowJob | null
}): PublishFailureViewModel {
  if (input.status !== 'FAILED') {
    return {
      hasFailure: false,
      title: '',
      stageLabel: '',
      reason: '',
      expectedFeeds: [],
      completedFeeds: [],
      failedFeeds: [],
      failedFeed: null,
      inventoryProcessingStarted: null,
    }
  }

  const result =
    input.job?.result && typeof input.job.result === 'object'
      ? (input.job.result as Record<string, any>)
      : {}

  const coordinated =
    result.coordinatedFailure && typeof result.coordinatedFailure === 'object'
      ? (result.coordinatedFailure as Record<string, any>)
      : {}

  const phase = String(coordinated.phase || result.failurePhase || '').trim()
  const inventoryProcessingStarted =
    typeof coordinated.inventoryProcessingStarted === 'boolean'
      ? coordinated.inventoryProcessingStarted
      : typeof result.inventoryProcessingStarted === 'boolean'
        ? result.inventoryProcessingStarted
        : null

  const expectedFeeds = stringArray(coordinated.expectedFeeds)
  const failedFeeds = stringArray(coordinated.failedFeeds)
  const failedFeedSet = new Set(failedFeeds)
  const completedFeeds = stringArray(coordinated.completedFeeds).filter((feed) => !failedFeedSet.has(feed))
  const failedFeed = nonEmpty(coordinated.failedFeed) || failedFeeds[0] || null
  const reason =
    nonEmpty(coordinated.message) ||
    nonEmpty(input.job?.errorMessage) ||
    'No failure reason was captured. Check logs with this correlation ID.'

  return {
    hasFailure: true,
    title: 'Publish failed',
    stageLabel: failurePhaseLabel(phase, inventoryProcessingStarted),
    reason,
    expectedFeeds,
    completedFeeds,
    failedFeeds,
    failedFeed,
    inventoryProcessingStarted,
  }
}

export function buildRunSummary(input: {
  job: PublishFlowJob | null
  artifacts: PublishArtifactsResponse | null
}): RunSummaryViewModel {
  const job = input.job
  const art = input.artifacts?.artifacts ?? null

  const status = String(job?.status || art?.status || 'UNKNOWN').toUpperCase()
  const startedAt = nonEmpty(job?.startedAt) || nonEmpty(art?.startedAt)
  const finishedAt = nonEmpty(job?.finishedAt) || nonEmpty(art?.finishedAt)
  const triggerTypeRaw = String(job?.triggerType || art?.triggerType || '').toLowerCase()
  const triggerType: RunSummaryViewModel['triggerType'] =
    triggerTypeRaw === 'user' ? 'user' : triggerTypeRaw === 'feed' ? 'feed' : 'unknown'

  const requestedByUsername =
    triggerType === 'user'
      ? nonEmpty(job?.requestedByUsername) || nonEmpty(art?.requestedByUsername) || null
      : null

  const feedName =
    nonEmpty(job?.feed) ||
    nonEmpty(art?.feed) ||
    (Array.isArray(art?.feeds) && art.feeds.length > 0 ? String(art.feeds[0]) : null)

  const fileKey = nonEmpty(job?.fileKey) || nonEmpty(art?.fileKey)

  return {
    status,
    statusColor: statusColor(status),
    triggerType,
    requestedByUsername,
    startedAt,
    finishedAt,
    durationMs: computeDurationMs(startedAt, finishedAt),
    feedName,
    fileKey,
    isTerminal: TERMINAL_STATUSES.has(status),
    failure: buildFailureViewModel({ status, job }),
  }
}

/* -------------------------------------------------------------------------- */
/* Publish outcome view-model                                                  */
/* -------------------------------------------------------------------------- */

/** Both outcome and Inventory Changes use the same complete-metrics authority. */
export function selectCompleteRunMetrics(input: {
  job: PublishFlowJob | null
  runMetrics: PublishRunMetrics | null
  runMetricsCounts?: PublishRunMetrics | null
}): PublishRunMetrics | null {
  const jobResult = input.job?.result && typeof input.job.result === 'object'
    ? input.job.result as Record<string, unknown>
    : null

  const metricsFromJob = normalizeRunMetrics(jobResult?.runMetrics)
  const fresh = input.runMetricsCounts
  return [fresh, metricsFromJob, input.runMetrics].find(metrics => metrics && !metrics.truncated) ?? null
}

export function buildPublishOutcome(input: {
  job: PublishFlowJob | null
  runMetrics: PublishRunMetrics | null
  runMetricsCounts?: PublishRunMetrics | null
}): PublishOutcomeViewModel {
  const jobResult = input.job?.result && typeof input.job.result === 'object'
    ? input.job.result as Record<string, unknown> : null
  const metrics = selectCompleteRunMetrics(input)
  const inventoryChanges = buildInventoryChanges(metrics)

  const liveWebsiteCount = numOrNull(jobResult?.opensearchListed)

  return {
    hasOutcome: liveWebsiteCount != null || inventoryChanges.hasMetrics,
    liveWebsiteCount,
    added: inventoryChanges.totals.added,
    removed: inventoryChanges.totals.removed,
    updated: inventoryChanges.totals.changed,
    madeVisible: inventoryChanges.totals.listed,
    hidden: inventoryChanges.totals.hidden,
  }
}

/**
 * Pick run metrics that include per-VIN lists suitable for dedupe.
 *
 * Job results intentionally store counts-only (`includeVins: false`) to keep
 * the status payload small. Publish Flow separately fetches run metrics with
 * `includeVins: true`. Prefer whichever source actually has `vins`; do NOT
 * mirror `buildPublishOutcome`'s job-first precedence here — bucket counts can
 * come from the job, but dedupe requires the fetched VIN lists.
 */
function resolveRunMetricsForVinDedupe(input: {
  job: PublishFlowJob | null
  runMetrics: PublishRunMetrics | null
}): PublishRunMetrics | null {
  const jobResult = input.job?.result && typeof input.job.result === 'object'
    ? (input.job.result as Record<string, unknown>)
    : null
  const fromJob = normalizeRunMetrics(jobResult?.runMetrics)
  const fromFetch = input.runMetrics

  const hasUsableVins = (m: PublishRunMetrics | null): m is PublishRunMetrics =>
    Boolean(m?.vins && !m.vinsTruncated && !m.truncated)

  if (hasUsableVins(fromFetch)) return fromFetch
  if (hasUsableVins(fromJob)) return fromJob
  return null
}

/**
 * Count UNIQUE VINs that changed on the website this publish, deduped across
 * every outcome bucket (added / removed / changed / listed / delisted) and
 * across both feed and manual scopes. A single VIN that was, say, both "added"
 * and "made visible" counts once — the bucket sum would double-count it.
 *
 * Returns `null` when no per-VIN lists are available (or they were truncated),
 * signalling callers to fall back to the bucket sum rather than undercount.
 */
export function countUniqueChangedWebsiteVins(input: {
  job: PublishFlowJob | null
  runMetrics: PublishRunMetrics | null
}): number | null {
  const metrics = resolveRunMetricsForVinDedupe(input)
  if (!metrics) return null

  const buckets = buildVinBuckets(metrics)
  if (buckets.length === 0) return null

  const unique = new Set<string>()
  for (const bucket of buckets) {
    for (const vin of bucket.vins) {
      const normalized = String(vin ?? '').trim().toUpperCase()
      if (normalized) unique.add(normalized)
    }
  }
  return unique.size
}

function normalizeRunMetrics(value: unknown): PublishRunMetrics | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  const feed = normalizeMetricBucket(raw.feed as Partial<PublishRunMetricBucket> | undefined)
  const manual = normalizeMetricBucket(raw.manual as Partial<PublishRunMetricBucket> | undefined)

  return {
    feed,
    manual,
    truncated: Boolean(raw.truncated),
    vinsTruncated: Boolean(raw.vinsTruncated),
    vins: raw.vins as PublishRunMetrics['vins'],
  }
}

function numOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/* -------------------------------------------------------------------------- */
/* Inventory changes view-model                                                */
/* -------------------------------------------------------------------------- */

export interface InventoryChangesBucket {
  added: number
  removed: number
  changed: number
  listed: number
  hidden: number
}

export interface InventoryChangesViewModel {
  /** True iff we have *any* metrics to render (otherwise show empty state). */
  hasMetrics: boolean
  feed: InventoryChangesBucket
  manual: InventoryChangesBucket
  totals: InventoryChangesBucket
  truncated: boolean
  vinsTruncated: boolean
}

const EMPTY_BUCKET: InventoryChangesBucket = {
  added: 0,
  removed: 0,
  changed: 0,
  listed: 0,
  hidden: 0,
}

export function buildInventoryChanges(metrics: PublishRunMetrics | null): InventoryChangesViewModel {
  if (!metrics) {
    return {
      hasMetrics: false,
      feed: { ...EMPTY_BUCKET },
      manual: { ...EMPTY_BUCKET },
      totals: { ...EMPTY_BUCKET },
      truncated: false,
      vinsTruncated: false,
    }
  }
  const feed = normalizeBucket(metrics.feed)
  const manual = normalizeBucket(metrics.manual)
  return {
    hasMetrics: true,
    feed,
    manual,
    totals: sumBuckets(feed, manual),
    truncated: Boolean(metrics.truncated),
    vinsTruncated: Boolean(metrics.vinsTruncated),
  }
}

/* -------------------------------------------------------------------------- */
/* VIN buckets view-model                                                      */
/* -------------------------------------------------------------------------- */

export type VinBucketKey = 'added' | 'removed' | 'changed' | 'listed' | 'hidden'
type PublishRunVinBucketKey = 'added' | 'removed' | 'changed' | 'listed' | 'delisted'
export type VinBucketScope = 'feed' | 'manual'

export interface VinBucketViewModel {
  scope: VinBucketScope
  key: VinBucketKey
  label: string
  vins: string[]
}

const VIN_BUCKET_LABELS: Record<VinBucketKey, string> = {
  added: 'Added',
  removed: 'Removed',
  changed: 'Changed',
  listed: 'Made Visible',
  hidden: 'Hidden',
}

const VIN_BUCKET_SOURCE_KEYS: Record<VinBucketKey, PublishRunVinBucketKey> = {
  added: 'added',
  removed: 'removed',
  changed: 'changed',
  listed: 'listed',
  hidden: 'delisted',
}

export function buildVinBuckets(metrics: PublishRunMetrics | null): VinBucketViewModel[] {
  if (!metrics?.vins) return []
  const out: VinBucketViewModel[] = []
  for (const scope of ['feed', 'manual'] as VinBucketScope[]) {
    const scopeVins = metrics.vins[scope]
    if (!scopeVins) continue
    for (const key of Object.keys(VIN_BUCKET_LABELS) as VinBucketKey[]) {
      const sourceKey = VIN_BUCKET_SOURCE_KEYS[key]
      const vins = Array.isArray(scopeVins[sourceKey]) ? scopeVins[sourceKey] : []
      if (vins.length === 0) continue
      out.push({
        scope,
        key,
        label: `${scope === 'feed' ? 'Feed' : 'Manual'} · ${VIN_BUCKET_LABELS[key]}`,
        vins,
      })
    }
  }
  return out
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

const TERMINAL_STATUSES = new Set([
  'SUCCEEDED',
  'FAILED',
  'SKIPPED',
  'FLAGGED_LARGE_DELTA',
])

function statusColor(status: string): RunSummaryViewModel['statusColor'] {
  switch (status) {
    case 'SUCCEEDED':
      return 'success'
    case 'FAILED':
      return 'error'
    case 'FLAGGED_LARGE_DELTA':
      return 'warning'
    case 'INITIATED':
    case 'PROCESSING':
      return 'info'
    case 'SKIPPED':
      return 'grey'
    default:
      return 'grey'
  }
}

function nonEmpty(value: unknown): string | null {
  if (value == null) return null
  const text = String(value).trim()
  return text.length > 0 ? text : null
}

function computeDurationMs(startedAt: string | null, finishedAt: string | null): number | null {
  if (!startedAt) return null
  const start = Date.parse(startedAt)
  if (!Number.isFinite(start)) return null
  const endMs = finishedAt ? Date.parse(finishedAt) : NaN
  if (!Number.isFinite(endMs)) return null
  return Math.max(0, endMs - start)
}

type PublishRunMetricBucket = {
  added: number
  removed: number
  changed: number
  listed: number
  delisted: number
}

function normalizeBucket(b: Partial<PublishRunMetricBucket> | undefined | null): InventoryChangesBucket {
  if (!b) return { ...EMPTY_BUCKET }
  return {
    added: numOrZero(b.added),
    removed: numOrZero(b.removed),
    changed: numOrZero(b.changed),
    listed: numOrZero(b.listed),
    hidden: numOrZero(b.delisted),
  }
}

function normalizeMetricBucket(b: Partial<PublishRunMetricBucket> | undefined | null): PublishRunMetricBucket {
  if (!b) return { added: 0, removed: 0, changed: 0, listed: 0, delisted: 0 }
  return {
    added: numOrZero(b.added),
    removed: numOrZero(b.removed),
    changed: numOrZero(b.changed),
    listed: numOrZero(b.listed),
    delisted: numOrZero(b.delisted),
  }
}

function sumBuckets(a: InventoryChangesBucket, b: InventoryChangesBucket): InventoryChangesBucket {
  return {
    added: a.added + b.added,
    removed: a.removed + b.removed,
    changed: a.changed + b.changed,
    listed: a.listed + b.listed,
    hidden: a.hidden + b.hidden,
  }
}

function numOrZero(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function describeError(reason: unknown): string {
  if (reason instanceof Error) return reason.message
  if (typeof reason === 'string') return reason
  try {
    return JSON.stringify(reason)
  } catch {
    return 'Unknown error'
  }
}
